/**
 * PaymentsPanel — REBUILT at PAY-05 (2026-08-07) as the plan's AUTOPAY surface.
 * The old scaffold's inert Process-payment / Issue-refund buttons are gone —
 * real collection lives on Invoices (Collect payment) and refunds live on the
 * payment rows. This panel owns: autopay enrollment (consent recorded
 * verbatim), the renewal preview (what will be charged, to which card, when),
 * decline visibility, and the plan's ledger history (agreement_id = this plan).
 *
 * The charging is done by the daily `plan-autopay` engine: renewal window →
 * ONE membership invoice per cycle → charge the card on file → the plan
 * advances a term. If the customer pays that invoice any OTHER way (portal,
 * office), the engine still advances the plan — autopay is a convenience, the
 * invoice is the truth. Declines retry daily, 3 strikes, office sees why here.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, ShieldCheck, Lock, AlertTriangle, RefreshCw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useOrgSettingJson } from "@/lib/orgSettings";
import { useCustomerPaymentProfile, cardBrandLabel, moneyExact } from "@/features/payments/payments";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { onErrToast } from "@/components/ui/mutationToast";

const CONSENT =
  "Customer authorized Sedgwick Heating to automatically charge the card on file for maintenance-plan renewals until autopay is turned off.";

interface PlanAutopayState {
  autopay: boolean;
  autopay_last_error: string | null;
  autopay_consent_at: string | null;
  autopay_consent_by: string | null;
  expire_date: string | null;
  total: number | null;
}
interface PlanPayment {
  id: string;
  amount: number;
  method: string | null;
  status: string;
  card_brand: string | null;
  card_last4: string | null;
  created_by: string | null;
  created_at: string;
}

const fmtDate = (s: string | null) =>
  s ? new Date(s.length <= 10 ? s + "T00:00:00" : s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

export function PaymentsPanel({ plan }: { plan: { id: string; custno: string } }) {
  const qc = useQueryClient();
  // 360-AUDIT wave 2 (C): autopay enrollment is billing consent — gated.
  const canAgree = useCan("agreements.edit");
  const live = useOrgSettingJson<boolean>("payments_live", false).data ?? false;
  const { data: profile } = useCustomerPaymentProfile(plan.custno);

  const { data: st } = useQuery({
    queryKey: ["plan_autopay", plan.id],
    queryFn: async (): Promise<PlanAutopayState | null> => {
      const { data } = await supabase
        .from("pegasus_plan_sale")
        .select("autopay, autopay_last_error, autopay_consent_at, autopay_consent_by, expire_date, total")
        .eq("id", plan.id)
        .maybeSingle();
      return (data as PlanAutopayState | null) ?? null;
    },
  });

  const { data: payments } = useQuery({
    queryKey: ["plan_payments", plan.id],
    queryFn: async (): Promise<PlanPayment[]> => {
      const { data } = await supabase
        .from("pegasus_payment")
        .select("id, amount, method, status, card_brand, card_last4, created_by, created_at")
        .eq("agreement_id", plan.id)
        .order("created_at", { ascending: false })
        .limit(8);
      return (data ?? []) as PlanPayment[];
    },
  });

  const toggle = useMutation({
    mutationFn: async (on: boolean) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("pegasus_plan_sale")
        .update(
          on
            ? {
                autopay: true,
                autopay_consent_text: CONSENT,
                autopay_consent_at: new Date().toISOString(),
                autopay_consent_by: u?.user?.email ?? null,
                autopay_last_error: null,
              }
            : { autopay: false },
        )
        .eq("id", plan.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plan_autopay", plan.id] }),
    // 360-AUDIT wave 2 (E): the checkbox silently snapped back on failure.
    onError: onErrToast("Couldn't change autopay"),
  });

  if (!st) return null;
  const hasCard = !!profile?.default_payment_method_id;
  const cardLabel = hasCard ? `${cardBrandLabel(profile?.card_brand)} ····${profile?.card_last4 ?? ""}` : null;
  const on = st.autopay;

  return (
    <div className="rounded-xl border border-hairline bg-white p-4 shadow-e1">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
          <RefreshCw className="h-3.5 w-3.5" />
        </span>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Autopay &amp; Billing</span>
        {st.total != null && (
          <span className="ml-auto text-xs text-slate-500">
            Renews {fmtDate(st.expire_date)} · <span className="font-semibold text-slate-800">{moneyExact(Number(st.total))}</span>
          </span>
        )}
      </div>

      {/* The enrollment row — a real consent moment, recorded verbatim. */}
      <label className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${on ? "border-emerald-200 bg-emerald-50/60" : "border-hairline bg-slate-50/60"} ${!hasCard && !on ? "opacity-70" : ""}`}>
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-emerald-600"
          checked={on}
          disabled={toggle.isPending || (!on && !hasCard) || !canAgree}
          onChange={(e) => toggle.mutate(e.target.checked)}
        />
        <span className="min-w-0 text-sm">
          <span className="font-semibold text-slate-800">Autopay this plan&apos;s renewals</span>
          {on ? (
            <>
              <span className="mt-0.5 block text-xs leading-relaxed text-slate-600">
                Next renewal {fmtDate(st.expire_date)}: <b className="tabular-nums">{moneyExact(Number(st.total ?? 0))}</b> to {cardLabel ?? "the card on file"}, then the plan advances automatically.
              </span>
              {st.autopay_consent_at && (
                <span className="mt-0.5 block text-[11px] text-slate-400">
                  Consent recorded {fmtDate(st.autopay_consent_at)}{st.autopay_consent_by ? ` by ${st.autopay_consent_by}` : ""}.
                </span>
              )}
            </>
          ) : (
            <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">
              {hasCard
                ? `The renewal will be charged to ${cardLabel} automatically — the customer said yes, consent gets recorded.`
                : "Needs a card on file first — take any payment with “Keep this card on file” checked, then flip this on."}
            </span>
          )}
        </span>
      </label>

      {st.autopay_last_error && (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium leading-relaxed text-red-700">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {st.autopay_last_error}
        </p>
      )}

      {!live && (
        <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-400">
          <Lock className="mt-0.5 h-3 w-3 shrink-0" />
          The renewal engine runs daily and is armed — no real charges until the payments switch flips at rollout.
        </p>
      )}

      {(payments ?? []).length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {payments!.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-xs">
              <span className="flex min-w-0 items-center gap-2">
                {p.created_by === "autopay" ? (
                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">Autopay</span>
                ) : (
                  <CreditCard className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                )}
                <span className="tabular-nums font-medium text-slate-700">{moneyExact(Number(p.amount))}</span>
                <span className="truncate text-slate-400">
                  {p.card_brand ? `${cardBrandLabel(p.card_brand)} ····${p.card_last4 ?? ""}` : p.method ?? ""} · {p.status}
                </span>
              </span>
              <span className="shrink-0 text-slate-400">{new Date(p.created_at).toLocaleDateString()}</span>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400">
        <ShieldCheck className="h-3 w-3" />
        Collecting a one-off plan payment? Use Collect payment on the Invoices tab — refunds live on the payment rows there too.
      </p>
    </div>
  );
}
